/**
 * IDDLWorkflow — Iter-18 candidate-facing simulation primitive.
 *
 * Realistic incident-response workspace for the CS-IR concepts
 * (Recognition · Containment · Reporting · Recovery). Designed
 * around three behavioural surfaces:
 *
 *   • ALERTS panel — inbound signals the candidate must triage.
 *                    Clicking an alert opens its details; the
 *                    "Flag as incident" / "Dismiss" buttons emit
 *                    the per-alert truth claim.
 *   • PLAN BUILDER — the candidate composes an ordered list of
 *                    response actions from a categorised palette
 *                    (contain · investigate · recover · communicate).
 *                    Unsafe actions appear in the palette too; if
 *                    added they show an inline warning and can be
 *                    removed before submitting. Reorder via ↑ / ↓.
 *   • ESCALATION   — a channel picker (SOC / IT ticket / public
 *                    Twitter / community forum / personal blog).
 *                    Picking an unsafe channel surfaces an inline
 *                    warning and can be changed before submitting.
 *
 * Per the iter-18 directive there are NO strict timers and NO
 * time-based auto-fail. `dwell_ms` is recorded as research signal
 * only. The scoring philosophy is: only the FINAL plan state is
 * scored (multi-object on `step_completed` + sequence_rules on
 * action-name events). Intermediate unsafe behaviour is logged but
 * always recoverable.
 *
 * Canonical events (15):
 *   workflow_opened               — once on mount
 *   alert_inspected               — click an alert row
 *   alert_flagged_as_incident     — declare alert is a real incident
 *   alert_dismissed               — declare alert is noise
 *   incident_recognized           — emitted at submit when the alert
 *                                   flagged matches alert.is_incident
 *   action_added                  — add an action step to the plan
 *   action_removed                — remove an action from the plan
 *   action_reordered              — move an action up / down
 *   unsafe_action_attempted       — added action is in action.unsafe
 *   unsafe_action_reverted        — removed a previously-added unsafe
 *   <action_id>                   — emitted directly (event_name =
 *                                   action.id) so sequence_rules can
 *                                   reason about ordering between two
 *                                   specific action names
 *   channel_selected              — pick an escalation channel
 *   unsafe_channel_attempted      — picked channel is in unsafe set
 *   channel_reverted              — switched from unsafe → safe channel
 *   safe_channel_selected         — emitted at submit if final channel
 *                                   is safe
 *   public_disclosure             — emitted at submit if final channel
 *                                   is unsafe (auto-fail per IR-03)
 *   step_completed                — emitted PER action in the final
 *                                   queue at submit (multi-object
 *                                   scorer keys on this)
 *   final_decision                — submit
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, Plus, Trash2,
  Send, ShieldAlert, CheckCircle2, Radio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

const ACTION_KIND_BADGE = {
  contain:     "bg-rose-100 text-rose-700 border-rose-300",
  investigate: "bg-sky-100 text-sky-700 border-sky-300",
  recover:     "bg-emerald-100 text-emerald-700 border-emerald-300",
  communicate: "bg-amber-100 text-amber-800 border-amber-300",
  unsafe:      "bg-rose-200 text-rose-900 border-rose-400",
};

export default function IDDLWorkflow({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  const alerts = scenarioText?.alerts || [];
  const actionsPalette = scenarioText?.actions || [];
  const channels = scenarioText?.channels || [];
  // iter-21 — DS Batch 7 added optional surface flags so the same
  // primitive can render a stripped-down workspace (e.g. channel-only
  // for DS-CM-02). Default behaviour is unchanged: every surface
  // renders unless the contract opts out.
  const showAlerts = scenarioText?.show_alerts !== false;
  const showPlanBuilder = scenarioText?.show_plan_builder !== false;
  const showChannels = scenarioText?.show_channels !== false && channels.length > 0;

  // Mutable state
  const [activeAlertId, setActiveAlertId] = useState(alerts[0]?.id || null);
  const [alertDecisions, setAlertDecisions] = useState({}); // {alert_id: 'flagged'|'dismissed'}
  const [plan, setPlan] = useState([]); // ordered [action_id, ...]
  const [channelId, setChannelId] = useState(null);
  const [channelWarnAck, setChannelWarnAck] = useState(false);
  const [actionWarnings, setActionWarnings] = useState({}); // {action_id: {since_ms, ack}}
  const [finalized, setFinalized] = useState(null);
  const openedRef = useRef(false);

  const actionsById = useMemo(() => {
    const map = {};
    for (const a of actionsPalette) map[a.id] = a;
    return map;
  }, [actionsPalette]);

  const channelsById = useMemo(() => {
    const map = {};
    for (const c of channels) map[c.id] = c;
    return map;
  }, [channels]);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("workflow_opened", {
      alert_count: alerts.length,
      action_count: actionsPalette.length,
      channel_count: channels.length,
    }, "workflow");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} failed: ${detail}`);
      return null;
    }
  }

  // ─── Alerts panel ────────────────────────────────────────────────────
  const onInspectAlert = (a) => {
    setActiveAlertId(a.id);
    emit("alert_inspected", { alert_id: a.id, label: a.label }, "alerts");
  };

  const onFlagAlert = (a) => {
    if (finalized) return;
    setAlertDecisions((s) => ({ ...s, [a.id]: "flagged" }));
    emit("alert_flagged_as_incident", {
      alert_id: a.id, label: a.label, claimed_incident: true,
      truth: a.is_incident,
    }, "alerts");
  };

  const onDismissAlert = (a) => {
    if (finalized) return;
    setAlertDecisions((s) => ({ ...s, [a.id]: "dismissed" }));
    emit("alert_dismissed", {
      alert_id: a.id, label: a.label, claimed_incident: false,
      truth: a.is_incident,
    }, "alerts");
  };

  // ─── Plan builder ────────────────────────────────────────────────────
  const onAddAction = (a) => {
    if (finalized) return;
    if (plan.includes(a.id)) return; // dedupe
    setPlan((p) => [...p, a.id]);
    // Generic event PLUS the action.id as a discrete event name —
    // the latter lets sequence_rules reason about ordering between
    // specific action names (e.g. network_disconnected before
    // password_changed).
    emit("action_added", {
      action_id: a.id, label: a.label, position: plan.length,
    }, "plan");
    emit(a.id, { action_id: a.id }, "plan");
    if (a.unsafe) {
      emit("unsafe_action_attempted", {
        action_id: a.id, label: a.label,
        risk_level: a.risk_level || "warn",
      }, "plan");
      setActionWarnings((w) => ({
        ...w, [a.id]: { since_ms: Date.now(), ack: false },
      }));
    }
  };

  const onRemoveAction = (actionId) => {
    if (finalized) return;
    const a = actionsById[actionId];
    setPlan((p) => p.filter((x) => x !== actionId));
    emit("action_removed", { action_id: actionId, label: a?.label }, "plan");
    if (a?.unsafe) {
      emit("unsafe_action_reverted", {
        action_id: actionId, label: a.label,
      }, "plan");
    }
    setActionWarnings((w) => {
      const next = { ...w };
      delete next[actionId];
      return next;
    });
  };

  const onMoveAction = (idx, direction) => {
    if (finalized) return;
    const j = idx + direction;
    if (j < 0 || j >= plan.length) return;
    setPlan((p) => {
      const next = [...p];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    emit("action_reordered", {
      from: idx, to: j, action_id: plan[idx],
    }, "plan");
  };

  const acknowledgeActionWarning = (actionId) => {
    const w = actionWarnings[actionId];
    if (!w) return;
    const dwellMs = Date.now() - (w.since_ms || Date.now());
    emit("warning_acknowledged", {
      action_id: actionId, dwell_ms: dwellMs,
    }, "plan");
    setActionWarnings((wm) => ({
      ...wm, [actionId]: { ...w, ack: true },
    }));
  };

  // ─── Channel picker ──────────────────────────────────────────────────
  const onPickChannel = (c) => {
    if (finalized) return;
    const prev = channelId ? channelsById[channelId] : null;
    setChannelId(c.id);
    setChannelWarnAck(false);
    emit("channel_selected", {
      channel_id: c.id, label: c.label, safe: !!c.safe,
    }, "escalate");
    if (!c.safe) {
      emit("unsafe_channel_attempted", {
        channel_id: c.id, label: c.label,
      }, "escalate");
    } else if (prev && prev.safe === false) {
      emit("channel_reverted", {
        from: prev.id, to: c.id,
      }, "escalate");
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────
  const submitFinal = () => {
    if (finalized) return;
    // Per-step report — multi-object scorer keys on this.
    for (const aid of plan) {
      const a = actionsById[aid] || {};
      emit("step_completed", {
        step_id: aid, message_id: aid,
        label: a.label, must_be_done: !!a.must_be_done,
      }, "plan");
    }
    // Per-channel report — multi-object scorer can also key on
    // safe_channel_selected as report_event when the contract is
    // channel-centric (CS-IR-03).
    if (channelId) {
      const c = channelsById[channelId] || {};
      if (c.safe) {
        emit("safe_channel_selected", {
          channel_id: channelId, message_id: channelId, label: c.label,
        }, "escalate");
      } else {
        emit("public_disclosure", {
          channel_id: channelId, label: c.label,
        }, "escalate");
      }
    }
    // Per-alert report — synthesise incident_recognized for any
    // alert correctly flagged as an incident.
    for (const a of alerts) {
      const decision = alertDecisions[a.id];
      if (decision === "flagged" && a.is_incident) {
        emit("incident_recognized", {
          alert_id: a.id, message_id: a.id, label: a.label,
        }, "alerts");
      }
    }
    // Detect "incident_unreported": there was an incident alert but
    // the candidate either didn't flag it OR didn't add any report
    // action. Per the iter-18 directive only the final state matters,
    // so the check runs at submit.
    const anyRealIncident = alerts.some((a) => a.is_incident);
    const anyIncidentFlagged = alerts.some(
      (a) => a.is_incident && alertDecisions[a.id] === "flagged",
    );
    const channelSafe = channelId
      ? !!(channelsById[channelId]?.safe)
      : false;
    if (anyRealIncident && (!anyIncidentFlagged || !channelSafe)) {
      emit("incident_unreported", {
        any_flagged: anyIncidentFlagged, channel_safe: channelSafe,
      }, "workflow");
    }
    emit("final_decision", {
      decision: channelSafe ? "reported" : "submitted",
      plan, channel_id: channelId,
      alert_decisions: { ...alertDecisions },
    }, "workflow");
    setFinalized(channelSafe ? "reported" : "submitted");
    onFinalize?.({ decision: channelSafe ? "reported" : "submitted" });
  };

  // ─── Render ──────────────────────────────────────────────────────────
  const activeAlert = alerts.find((a) => a.id === activeAlertId);
  const planActions = plan.map((id) => actionsById[id]).filter(Boolean);
  const unsafeInPlanCount = planActions.filter((a) => a.unsafe).length;
  const channelSelected = channelId ? channelsById[channelId] : null;
  const channelUnsafe =
    channelSelected && channelSelected.safe === false && !channelWarnAck;

  return (
    <div
      className="border border-[var(--iddl-rule)] bg-white p-3 space-y-3"
      data-testid="iddl-workflow"
    >
      {/* Top — Alerts feed */}
      {showAlerts && (
      <section
        className="border border-[var(--iddl-rule)] p-3"
        data-testid="iddl-wf-alerts"
      >
        <div className="label-mono mb-2 flex items-center gap-2">
          <Activity size={12} />
          {t("sim.wf.alerts_title")}
        </div>
        <div className="grid md:grid-cols-3 gap-2 mb-2">
          {alerts.map((a) => {
            const decision = alertDecisions[a.id];
            const active = a.id === activeAlertId;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onInspectAlert(a)}
                className={`text-left text-sm border p-2 ${
                  active
                    ? "border-[var(--iddl-ink)] bg-[var(--iddl-paper)]"
                    : "border-[var(--iddl-rule)] bg-white hover:bg-[var(--iddl-paper)]"
                }`}
                data-testid={`iddl-wf-alert-${a.id}`}
              >
                <div className="font-semibold text-xs mb-1">{a.id}</div>
                <div className="text-xs mb-2">{a.label}</div>
                {decision && (
                  <span
                    className={`text-[10px] font-mono px-1.5 border ${
                      decision === "flagged"
                        ? "border-rose-400 bg-rose-100 text-rose-700"
                        : "border-zinc-300 bg-zinc-50 text-zinc-700"
                    }`}
                    data-testid={`iddl-wf-alert-decision-${a.id}`}
                  >
                    {decision === "flagged"
                      ? t("sim.wf.alert_flagged")
                      : t("sim.wf.alert_dismissed")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {activeAlert && !finalized && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => onFlagAlert(activeAlert)}
              className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
              data-testid={`iddl-wf-btn-flag-${activeAlert.id}`}
            >
              <AlertTriangle size={12} className="mr-1" />
              {t("sim.wf.btn_flag_incident")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDismissAlert(activeAlert)}
              className="rounded-none"
              data-testid={`iddl-wf-btn-dismiss-${activeAlert.id}`}
            >
              {t("sim.wf.btn_dismiss")}
            </Button>
          </div>
        )}
      </section>
      )}

      {/* Middle — Plan builder */}
      {showPlanBuilder && (
      <section
        className="border border-[var(--iddl-rule)] p-3"
        data-testid="iddl-wf-plan"
      >
        <div className="label-mono mb-2 flex items-center gap-2">
          <ShieldAlert size={12} />
          {t("sim.wf.plan_title")}
          <span className="ms-auto text-xs text-[var(--iddl-muted)]">
            {t("sim.wf.plan_count", { n: plan.length })}
            {unsafeInPlanCount > 0 && (
              <span className="ms-2 text-rose-700">
                · {t("sim.wf.unsafe_count", { n: unsafeInPlanCount })}
              </span>
            )}
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {/* Palette */}
          <div data-testid="iddl-wf-palette">
            <div className="label-mono mb-1">{t("sim.wf.palette_title")}</div>
            <div className="space-y-1.5">
              {actionsPalette.map((a) => {
                const kind = a.unsafe ? "unsafe" : (a.kind || "investigate");
                const inPlan = plan.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onAddAction(a)}
                    disabled={!!finalized || inPlan}
                    className={`w-full text-left text-sm border p-2 flex items-center gap-2 ${
                      inPlan ? "bg-zinc-50 border-zinc-200 text-zinc-400"
                             : "bg-white border-[var(--iddl-rule)] hover:bg-[var(--iddl-paper)]"
                    }`}
                    data-testid={`iddl-wf-palette-${a.id}`}
                  >
                    <Plus size={12} className="shrink-0" />
                    <span className="flex-1">{a.label}</span>
                    <span className={`text-[10px] font-mono px-1.5 border ${ACTION_KIND_BADGE[kind] || "border-zinc-300"}`}>
                      {a.unsafe ? t("sim.wf.kind_unsafe") : (a.kind || "investigate").toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plan queue */}
          <div data-testid="iddl-wf-queue">
            <div className="label-mono mb-1">{t("sim.wf.queue_title")}</div>
            {plan.length === 0 && (
              <div className="text-xs text-[var(--iddl-muted)] italic">
                {t("sim.wf.queue_empty")}
              </div>
            )}
            <ol className="space-y-1.5">
              {plan.map((aid, idx) => {
                const a = actionsById[aid];
                if (!a) return null;
                const warn = actionWarnings[aid];
                const showWarn = !!warn && !warn.ack && a.unsafe;
                return (
                  <li
                    key={aid}
                    className={`border p-2 text-sm ${
                      a.unsafe ? "border-rose-400 bg-rose-50/40"
                              : "border-[var(--iddl-rule)] bg-white"
                    }`}
                    data-testid={`iddl-wf-queue-${aid}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-[var(--iddl-muted)] w-5">
                        {idx + 1}.
                      </span>
                      <span className="flex-1">{a.label}</span>
                      <button
                        type="button"
                        onClick={() => onMoveAction(idx, -1)}
                        disabled={!!finalized || idx === 0}
                        className="border border-[var(--iddl-rule)] p-1 bg-white disabled:opacity-30"
                        data-testid={`iddl-wf-queue-up-${aid}`}
                      >
                        <ArrowUp size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveAction(idx, +1)}
                        disabled={!!finalized || idx === plan.length - 1}
                        className="border border-[var(--iddl-rule)] p-1 bg-white disabled:opacity-30"
                        data-testid={`iddl-wf-queue-down-${aid}`}
                      >
                        <ArrowDown size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveAction(aid)}
                        disabled={!!finalized}
                        className="border border-rose-300 p-1 bg-white text-rose-700"
                        data-testid={`iddl-wf-queue-remove-${aid}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                    {showWarn && (
                      <div
                        className="mt-2 border-2 border-rose-500 bg-rose-50 p-2 text-xs"
                        data-testid={`iddl-wf-action-warning-${aid}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <AlertTriangle size={12} className="text-rose-700 mt-0.5" />
                          <div className="text-rose-900">
                            <div className="font-semibold mb-1">
                              {t("sim.wf.warning_title")}
                            </div>
                            <div>{a.warning_text || t("sim.wf.warning_default")}</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onRemoveAction(aid)}
                            className="rounded-none border-emerald-500 text-emerald-800 hover:bg-emerald-50"
                            data-testid={`iddl-wf-action-warning-remove-${aid}`}
                          >
                            {t("sim.wf.warning_remove")}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => acknowledgeActionWarning(aid)}
                            className="rounded-none btn-ink"
                            data-testid={`iddl-wf-action-warning-ack-${aid}`}
                          >
                            {t("sim.wf.warning_ack")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>
      )}

      {/* Escalation */}
      {showChannels && (
        <section
          className="border border-[var(--iddl-rule)] p-3"
          data-testid="iddl-wf-escalate"
        >
          <div className="label-mono mb-2 flex items-center gap-2">
            <Radio size={12} />
            {t("sim.wf.escalate_title")}
          </div>
          <div className="grid md:grid-cols-2 gap-2 mb-2">
            {channels.map((c) => {
              const active = channelId === c.id;
              const unsafe = c.safe === false;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onPickChannel(c)}
                  disabled={!!finalized}
                  className={`text-left text-sm border-2 p-2 ${
                    active
                      ? (unsafe
                          ? "border-rose-500 bg-rose-50"
                          : "border-emerald-500 bg-emerald-50")
                      : "border-zinc-200 bg-white hover:bg-[var(--iddl-paper)]"
                  }`}
                  data-testid={`iddl-wf-channel-${c.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full border border-zinc-400"
                      style={{ background: active ? "#0f172a" : "transparent" }} />
                    <span className="flex-1">{c.label}</span>
                    {unsafe && (
                      <span className="text-[10px] font-mono px-1.5 border border-rose-400 bg-rose-100 text-rose-700">
                        {t("sim.wf.kind_unsafe")}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {channelUnsafe && (
            <div
              className="border-2 border-rose-500 bg-rose-50 p-2 text-xs"
              data-testid="iddl-wf-channel-warning"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={12} className="text-rose-700 mt-0.5" />
                <div className="text-rose-900">
                  <div className="font-semibold mb-1">
                    {t("sim.wf.warning_title")}
                  </div>
                  <div>
                    {channelSelected.warning_text || t("sim.wf.channel_warning_default")}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const safe = channels.find((x) => x.safe);
                    if (safe) onPickChannel(safe);
                  }}
                  className="rounded-none border-emerald-500 text-emerald-800 hover:bg-emerald-50"
                  data-testid="iddl-wf-channel-warning-revert"
                >
                  {t("sim.wf.warning_revert_channel")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setChannelWarnAck(true)}
                  className="rounded-none btn-ink"
                  data-testid="iddl-wf-channel-warning-ack"
                >
                  {t("sim.wf.warning_ack")}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Footer — Final submit */}
      <div
        className="border-t border-[var(--iddl-rule)] pt-3 flex items-center gap-3"
        data-testid="iddl-wf-final"
      >
        <div className="text-sm flex-1">
          {unsafeInPlanCount > 0 || (channelSelected && !channelSelected.safe) ? (
            <span className="text-rose-700 flex items-center gap-1.5">
              <AlertTriangle size={12} />
              {t("sim.wf.unsafe_state_summary")}
            </span>
          ) : (
            <span className="text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 size={12} />
              {t("sim.wf.safe_state_summary")}
            </span>
          )}
        </div>
        <Button
          onClick={submitFinal}
          disabled={!!finalized}
          className="rounded-none btn-ink"
          data-testid="iddl-wf-btn-submit"
        >
          <Send size={12} className="mr-2" />
          {t("sim.wf.btn_submit")}
        </Button>
      </div>
      {finalized && (
        <div className="text-xs text-[var(--iddl-muted)]" data-testid="iddl-wf-finalized">
          {t("sim.wf.finalized", { decision: finalized })}
        </div>
      )}
    </div>
  );
}
